import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { DepartmentCard, NewsCard, EventCard, StatCard } from "@/components/Cards";
import { ROUTE_PATHS, departments, newsItems, events, facultyMembers } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem, scaleIn } from "@/lib/motion";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, GraduationCap, Users, BookOpen, Award, ExternalLink, Calendar, MapPin } from "lucide-react";

export default function Home() {
  const stats = [
    { label: "طالب دارس حالياً", value: 300, icon: Users },
    { label: "خريج بكالوريوس", value: 160, icon: GraduationCap },
    { label: "خريج دبلوم عالي", value: 800, icon: Award },
    { label: "خريج دبلوم متوسط", value: 100, icon: BookOpen },
    { label: "تخصصات أكاديمية", value: 6, icon: BookOpen },
    { label: "مستويات دراسية", value: 4, icon: Award },
  ];

  const services = [
    {
      title: "بوابة الطالب",
      description: "الوصول إلى السجل الأكاديمي والدرجات",
      icon: Users,
      link: "https://xlserver.al-emtiaz.net//XLWeb/Default.aspx?u=ColTechGateLibya",
    },
    {
      title: "المكتبة الرقمية",
      description: "مصادر علمية وكتب إلكترونية",
      icon: BookOpen,
      link: "#",
    },
    {
      title: "التعلم الإلكتروني",
      description: "منصة المودل للمحاضرات والواجبات",
      icon: GraduationCap,
      link: "https://cgot.edu.ly/moodle44",
    },
  ];

  return (
    <Layout>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/888.jpg"
            alt="مبنى كلية بوابة التكنولوجيا"
            className="w-full h-full object-cover opacity-55"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/75" />
          {/* Cover bottom watermark */}
          <div className="absolute bottom-0 left-0 right-0 h-[25%]" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)' }} />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-4xl mx-auto"
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-3 text-white"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.8)' }}
              variants={scaleIn}
            >
              كلية بوابة التكنولوجيا
            </motion.h1>
            <motion.p
              className="text-sm md:text-base text-white/70 mb-4 tracking-widest font-light"
              variants={fadeInUp}
            >
              Technology Gateway College
            </motion.p>
            <motion.p
              className="text-xl md:text-2xl text-white/90 mb-10"
              style={{ textShadow: '0 1px 12px rgba(0,0,0,0.7)' }}
              variants={fadeInUp}
            >
              بوابتك نحو مستقبل تقني مشرق
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4 justify-center mb-10"
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem}>
                <Button
                  asChild
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-lg px-8 py-6 shadow-2xl hover:shadow-accent/40 transition-all"
                >
                  <Link to={ROUTE_PATHS.ADMISSIONS} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>التسجيل الآن</Link>
                </Button>
              </motion.div>
              <motion.div variants={staggerItem}>
                <Button
                  asChild
                  size="lg"
                  className="border-2 border-white/70 text-white hover:bg-white hover:text-primary font-semibold text-lg px-8 py-6 shadow-2xl transition-all backdrop-blur-sm bg-white/10"
                >
                  <Link to={ROUTE_PATHS.DEPARTMENTS} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>استكشف البرامج</Link>
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              className="flex flex-wrap gap-4 justify-center items-center"
              variants={staggerContainer}
            >
              <motion.div
                variants={staggerItem}
                className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 px-4 py-2 rounded-full"
              >
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-white">معتمدة من هيئة ضمان الجودة</span>
              </motion.div>
              <motion.div
                variants={staggerItem}
                className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 px-4 py-2 rounded-full"
              >
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-white">وزارة التعليم التقني والفني</span>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
          >
            {stats.map((stat) => (
              <motion.div key={stat.label} variants={staggerItem}>
                <StatCard {...stat} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-5xl mx-auto text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">عن الكلية</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12">
              كلية بوابة التكنولوجيا هي مؤسسة تعليمية خاصة معتمدة من وزارة التعليم التقني والمهني في ليبيا.
              نسعى لتقديم تعليم تقني متميز يواكب متطلبات سوق العمل المحلي والعالمي.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-right">
              <Card className="p-6 bg-card hover:shadow-lg transition-shadow border-t-4 border-t-primary">
                <h3 className="text-xl font-semibold mb-3 text-primary flex items-center gap-2">
                  <Award className="w-6 h-6" />
                  رؤيتنا
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  أن نكون المؤسسة التعليمية الرائدة في مجال التعليم التقني والمهني في ليبيا، ونساهم في بناء جيل من الكوادر المؤهلة القادرة على قيادة التحول الرقمي.
                </p>
              </Card>
              <Card className="p-6 bg-card hover:shadow-lg transition-shadow border-t-4 border-t-accent">
                <h3 className="text-xl font-semibold mb-3 text-accent flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  رسالتنا
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  تقديم برامج أكاديمية متميزة تجمع بين النظرية والتطبيق، وتزويد الطلاب بالمهارات والمعارف اللازمة للنجاح في سوق العمل المتغير.
                </p>
              </Card>
              <Card className="p-6 bg-card hover:shadow-lg transition-shadow border-t-4 border-t-green-500">
                <h3 className="text-xl font-semibold mb-3 text-green-600 flex items-center gap-2">
                  <GraduationCap className="w-6 h-6" />
                  أهدافنا
                </h3>
                <ul className="text-muted-foreground text-sm leading-relaxed space-y-1 text-right">
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-1">•</span>تطوير مناهج تعليمية متجددة تواكب التقنيات الحديثة</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-1">•</span>تعزيز الشراكات مع القطاع الخاص وسوق العمل</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-1">•</span>توفير بيئة تعليمية متميزة ومجهزة بأحدث التقنيات</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-1">•</span>تنمية مهارات الطلاب في البحث العلمي والابتكار</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 mt-1">•</span>تخريج كوادر مؤهلة تساهم في تنمية المجتمع الليبي</li>
                </ul>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>


      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">آخر الأخبار</h2>
            <p className="text-lg text-muted-foreground">تابع أحدث الأخبار والفعاليات</p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {newsItems.slice(0, 3).map((news) => (
              <motion.div key={news.id} variants={staggerItem}>
                <NewsCard news={news} />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <Button asChild size="lg" variant="outline">
              <Link to={ROUTE_PATHS.NEWS} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                عرض جميع الأخبار
                <ArrowLeft className="mr-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">الفعاليات القادمة</h2>
            <p className="text-lg text-muted-foreground">لا تفوت الفرصة للمشاركة</p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {events.slice(0, 3).map((event) => (
              <motion.div key={event.id} variants={staggerItem}>
                <EventCard event={event} />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <Button asChild size="lg" variant="outline">
              <Link to={ROUTE_PATHS.NEWS} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                عرض جميع الفعاليات
                <ArrowLeft className="mr-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">الخدمات الطلابية</h2>
            <p className="text-lg text-muted-foreground">وصول سريع للخدمات الإلكترونية</p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {services.map((service, index) => (
              <motion.div key={index} variants={staggerItem}>
                <Card className="p-6 text-center hover:shadow-lg transition-all group cursor-pointer">
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <service.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                    <p className="text-muted-foreground mb-4">{service.description}</p>
                    <div className="flex items-center justify-center gap-2 text-primary group-hover:gap-3 transition-all">
                      <span className="text-sm font-medium">الدخول</span>
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  </a>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>


      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.COLLEGE_BG_8}
            alt="التسجيل"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background/80" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">ابدأ رحلتك الأكاديمية معنا</h2>
            <p className="text-lg text-muted-foreground mb-8">
              التسجيل مفتوح الآن للفصل الدراسي الربيعي 2026. انضم إلى أكثر من 1500 طالب يدرسون في
              كلية بوابة التكنولوجيا.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
              >
                <Link to={ROUTE_PATHS.ADMISSIONS} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>سجل الآن</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
              >
                <Link to={ROUTE_PATHS.CONTACT} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>اتصل بنا</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
